/**
 * Center pane: full conversation thread with date separators, media
 * rendering, the AI draft review card, and a composer with canned replies
 * ("/" shortcut) + AI smart compose.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertCircle,
  ArrowLeft,
  Bot,
  Check,
  CheckCheck,
  Clock,
  FileText,
  Info,
  MapPin,
  Mic,
  Paperclip,
  Pause,
  Play,
  Send,
  Sparkles,
  Ticket,
  X,
  Zap,
} from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { ErrorState } from '@/components/ui/ErrorState';
import { Skeleton } from '@/components/ui/Skeleton';
import {
  crmMediaUrl,
  useApproveDraft,
  useCannedReplies,
  useCloseConversation,
  useConversationResumeAi,
  useConversationTakeover,
  useCreateConversationTicket,
  useCrmConversation,
  useDismissDraft,
  useSendConversationMessage,
  useSuggestReply,
  type CrmCannedReply,
  type CrmConversation,
  type CrmMediaItem,
  type CrmMessage,
} from '@/api/bi/crmConversations';
import { notify } from '@/lib/toast';
import { fmt } from '@/lib/format';
import { cn } from '@/lib/cn';
import { ChannelIcon, channelMeta, ContactAvatar, contactLabel, STATUS_META } from './channelMeta';

export function ThreadPane({
  convId,
  onBack,
  onToggleContext,
}: {
  convId: number;
  onBack: () => void;
  onToggleContext: () => void;
}) {
  const detail = useCrmConversation(convId);
  const conv = detail.data;

  if (detail.isLoading) {
    return (
      <div className="flex h-full flex-col gap-3 rounded-xl border border-border bg-surface p-4">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="flex-1" />
        <Skeleton className="h-20 w-full" />
      </div>
    );
  }
  if (detail.isError || !conv) {
    return (
      <div className="flex h-full items-center justify-center rounded-xl border border-border bg-surface">
        <ErrorState title="Conversation not found" onRetry={() => detail.refetch()} />
      </div>
    );
  }
  return <ThreadInner conv={conv} onBack={onBack} onToggleContext={onToggleContext} />;
}

function ThreadInner({
  conv,
  onBack,
  onToggleContext,
}: {
  conv: CrmConversation;
  onBack: () => void;
  onToggleContext: () => void;
}) {
  const convId = conv.id;
  const qc = useQueryClient();
  const send = useSendConversationMessage(convId);
  const approve = useApproveDraft(convId);
  const dismiss = useDismissDraft(convId);
  const takeover = useConversationTakeover(convId);
  const resume = useConversationResumeAi(convId);
  const createTicket = useCreateConversationTicket(convId);
  const close = useCloseConversation(convId);

  const [composer, setComposer] = useState('');
  const [draftEdit, setDraftEdit] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);
  const messages = conv.messages ?? [];

  useEffect(() => {
    if (conv.pending_draft?.draft) setDraftEdit(conv.pending_draft.draft);
  }, [conv.pending_draft?.draft]);

  const lastMessageId = messages.length ? messages[messages.length - 1].id : 0;
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [lastMessageId, convId]);

  async function handleSend(text: string) {
    const body = text.trim();
    if (!body) return;
    setComposer('');
    // Optimistic append so the bubble shows instantly; WS/refetch reconciles.
    const optimistic: CrmMessage = {
      id: -Date.now(),
      conversation_id: convId,
      direction: 'out',
      sender_type: 'agent',
      sender_user_id: null,
      body,
      media: [],
      external_id: '',
      delivery_status: 'pending',
      delivery_error: '',
      ai_meta: {},
      created_at: new Date().toISOString(),
    };
    qc.setQueryData<CrmConversation>(['bi', 'crm', 'conversations', convId], (old) =>
      old ? { ...old, messages: [...(old.messages ?? []), optimistic] } : old,
    );
    try {
      await send.mutateAsync(body);
    } catch {
      setComposer(body); // give the text back for retry
      notify.error('Send failed');
      void qc.invalidateQueries({ queryKey: ['bi', 'crm', 'conversations', convId] });
    }
  }

  const groups = useMemo(() => groupByDay(messages), [messages]);
  const status = STATUS_META[conv.status] ?? { label: conv.status, tone: 'neutral' as const };
  const name = contactLabel(conv.contact);

  return (
    <div className="flex h-full min-h-0 flex-col rounded-xl border border-border bg-surface">
      {/* Header */}
      <div className="flex items-center gap-2.5 border-b border-border px-3 py-2.5">
        <button
          type="button"
          onClick={onBack}
          className="rounded-md p-1.5 hover:bg-muted lg:hidden"
          aria-label="Back to list"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <ContactAvatar name={name} channel={conv.channel} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate text-sm font-semibold">{name}</span>
            <Badge tone={status.tone}>{status.label}</Badge>
            {conv.ai_paused ? <Badge tone="neutral">AI paused</Badge> : null}
          </div>
          <p className="truncate text-[11px] text-muted-foreground">
            <ChannelIcon channel={conv.channel} className="mr-1 inline" />
            {channelMeta(conv.channel).label}
            {conv.subject ? ` · ${conv.subject}` : ''}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {conv.ai_paused ? (
            <HeaderAction
              title="Resume AI"
              icon={<Play className="h-3.5 w-3.5" />}
              onClick={async () => {
                await resume.mutateAsync();
                notify.success('AI resumed');
              }}
            />
          ) : (
            <HeaderAction
              title="Take over from AI"
              icon={<Pause className="h-3.5 w-3.5" />}
              onClick={async () => {
                await takeover.mutateAsync();
                notify.success('You took over — AI paused');
              }}
            />
          )}
          <HeaderAction
            title="Create escalation ticket"
            icon={<Ticket className="h-3.5 w-3.5" />}
            onClick={async () => {
              const res = await createTicket.mutateAsync(undefined);
              notify.success(`Ticket #${res.ticket_id} created`);
            }}
          />
          {conv.linked_ticket_id ? (
            <Link
              to={`/crm/tickets/${conv.linked_ticket_id}`}
              className="rounded-md px-2 py-1.5 text-[11px] font-medium text-primary hover:bg-muted"
            >
              Ticket #{conv.linked_ticket_id}
            </Link>
          ) : null}
          {conv.status !== 'closed' ? (
            <HeaderAction
              title="Close conversation"
              icon={<X className="h-3.5 w-3.5" />}
              onClick={async () => {
                await close.mutateAsync();
                notify.success('Conversation closed');
              }}
            />
          ) : null}
          <HeaderAction
            title="Customer details"
            icon={<Info className="h-3.5 w-3.5" />}
            onClick={onToggleContext}
            className="xl:hidden"
          />
        </div>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto px-3 py-3 sm:px-4">
        {groups.map((group) => (
          <div key={group.day}>
            <div className="my-3 flex items-center gap-3">
              <div className="h-px flex-1 bg-border" />
              <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                {group.day}
              </span>
              <div className="h-px flex-1 bg-border" />
            </div>
            <div className="space-y-1">
              {group.messages.map((m, i) => (
                <MessageBubble
                  key={m.id}
                  message={m}
                  compact={i > 0 && sameGroup(group.messages[i - 1], m)}
                />
              ))}
            </div>
          </div>
        ))}
        {messages.length === 0 ? (
          <p className="py-12 text-center text-xs text-muted-foreground">No messages yet.</p>
        ) : null}
      </div>

      {/* AI draft review */}
      {conv.pending_draft?.draft ? (
        <div className="mx-3 mb-2 rounded-lg border border-info/40 bg-info/5 p-3">
          <div className="mb-2 flex flex-wrap items-center gap-2 text-xs font-semibold">
            <Bot className="h-4 w-4 text-info" /> AI draft — review before sending
            {conv.pending_draft.intent ? <Badge tone="neutral">{conv.pending_draft.intent}</Badge> : null}
            {typeof conv.pending_draft.confidence === 'number' ? (
              <Badge tone="neutral">{(conv.pending_draft.confidence * 100).toFixed(0)}% confident</Badge>
            ) : null}
          </div>
          <textarea
            className="min-h-[72px] w-full rounded-md border border-border bg-surface px-3 py-2 text-sm"
            value={draftEdit}
            onChange={(e) => setDraftEdit(e.target.value)}
          />
          <div className="mt-2 flex flex-wrap gap-2">
            <Button
              size="sm"
              className="gap-1"
              disabled={approve.isPending || !draftEdit.trim()}
              onClick={async () => {
                try {
                  await approve.mutateAsync(draftEdit);
                  notify.success('Draft sent');
                } catch {
                  notify.error('Could not send draft');
                }
              }}
            >
              <Check className="h-3.5 w-3.5" /> Approve & send
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="gap-1"
              disabled={dismiss.isPending}
              onClick={async () => {
                await dismiss.mutateAsync();
                notify.success('Draft dismissed');
              }}
            >
              <X className="h-3.5 w-3.5" /> Dismiss
            </Button>
          </div>
        </div>
      ) : null}

      {/* Composer */}
      <Composer
        conv={conv}
        value={composer}
        onChange={setComposer}
        onSend={handleSend}
        sending={send.isPending}
      />
    </div>
  );
}

function HeaderAction({
  title,
  icon,
  onClick,
  className,
}: {
  title: string;
  icon: React.ReactNode;
  onClick: () => void | Promise<void>;
  className?: string;
}) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      onClick={() => void onClick()}
      className={cn('rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground', className)}
    >
      {icon}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Message rendering
// ---------------------------------------------------------------------------

function groupByDay(messages: CrmMessage[]): Array<{ day: string; messages: CrmMessage[] }> {
  const out: Array<{ day: string; messages: CrmMessage[] }> = [];
  for (const m of messages) {
    const day = m.created_at ? fmt.date(m.created_at, 'EEE, d MMM yyyy') : 'Unknown';
    const last = out[out.length - 1];
    if (last && last.day === day) last.messages.push(m);
    else out.push({ day, messages: [m] });
  }
  return out;
}

function sameGroup(prev: CrmMessage, cur: CrmMessage): boolean {
  if (prev.direction !== cur.direction || prev.sender_type !== cur.sender_type) return false;
  if (!prev.created_at || !cur.created_at) return false;
  return new Date(cur.created_at).getTime() - new Date(prev.created_at).getTime() < 5 * 60_000;
}

const SENDER_LABEL: Record<string, string> = {
  customer: 'Customer',
  ai: 'Fasto AI',
  agent: 'Agent',
  system: 'System',
};

function MessageBubble({ message: m, compact }: { message: CrmMessage; compact: boolean }) {
  const mine = m.direction === 'out';
  if (m.sender_type === 'system') {
    return (
      <p className="py-1 text-center text-[11px] italic text-muted-foreground">
        {m.body} · {m.created_at ? fmt.time(m.created_at) : ''}
      </p>
    );
  }
  return (
    <div className={cn('flex', mine ? 'justify-end' : 'justify-start', compact ? 'mt-0.5' : 'mt-2')}>
      <div
        className={cn(
          'max-w-[85%] rounded-2xl px-3 py-2 text-sm sm:max-w-[70%]',
          mine
            ? 'rounded-br-md bg-primary text-primary-foreground'
            : 'rounded-bl-md bg-muted text-foreground',
        )}
      >
        {!compact ? (
          <div className={cn('mb-1 flex items-center gap-1 text-[10px]', mine ? 'opacity-80' : 'text-muted-foreground')}>
            {m.sender_type === 'ai' ? <Bot className="h-3 w-3" /> : null}
            <span className="font-semibold">{SENDER_LABEL[m.sender_type] ?? m.sender_type}</span>
          </div>
        ) : null}
        {m.body ? <p className="whitespace-pre-wrap break-words">{m.body}</p> : null}
        <MediaAttachments message={m} mine={mine} />
        <div
          className={cn(
            'mt-1 flex items-center justify-end gap-1 text-[10px]',
            mine ? 'opacity-75' : 'text-muted-foreground',
          )}
        >
          <span>{m.created_at ? fmt.time(m.created_at) : ''}</span>
          {mine ? <DeliveryTick status={m.delivery_status} error={m.delivery_error} /> : null}
        </div>
      </div>
    </div>
  );
}

function MediaAttachments({ message, mine }: { message: CrmMessage; mine: boolean }) {
  const items = (Array.isArray(message.media) ? message.media : []).filter(
    (m): m is CrmMediaItem => typeof m === 'object' && m !== null,
  );
  if (items.length === 0) return null;
  return (
    <div className="mt-1.5 space-y-1.5">
      {items.map((item, i) => (
        <MediaItemView key={i} item={item} url={mediaSrc(message.id, i, item)} mine={mine} />
      ))}
    </div>
  );
}

function mediaSrc(messageId: number, index: number, item: CrmMediaItem): string {
  if (item.path) return crmMediaUrl(messageId, index);
  return item.url || '';
}

function MediaItemView({ item, url, mine }: { item: CrmMediaItem; url: string; mine: boolean }) {
  const mime = item.mime_type || '';
  const isImage = mime.startsWith('image/') || item.kind === 'image' || item.kind === 'sticker';
  const isAudio = mime.startsWith('audio/') || item.kind === 'audio';
  const isVideo = mime.startsWith('video/') || item.kind === 'video';
  const isLocation = item.kind === 'location';

  if (isLocation && item.latitude != null && item.longitude != null) {
    return (
      <a
        href={`https://www.google.com/maps?q=${item.latitude},${item.longitude}`}
        target="_blank"
        rel="noreferrer"
        className={cn(
          'inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium ring-1 ring-inset',
          mine ? 'bg-primary-foreground/15 ring-primary-foreground/30' : 'bg-surface ring-border',
        )}
      >
        <MapPin className="h-3.5 w-3.5" />
        {item.name || 'Shared location'} · open map
      </a>
    );
  }
  if (isImage && url) {
    return (
      <a href={url} target="_blank" rel="noreferrer" className="block">
        <img
          src={url}
          alt={item.caption || item.filename || 'attachment'}
          loading="lazy"
          className="max-h-64 max-w-full rounded-lg object-cover ring-1 ring-border"
        />
        {item.caption ? <span className="mt-0.5 block text-[11px] opacity-80">{item.caption}</span> : null}
      </a>
    );
  }
  if (isAudio) {
    return (
      <div className="space-y-1">
        {url ? <audio controls preload="none" src={url} className="h-9 max-w-full" /> : null}
        {item.transcript ? (
          <p className={cn('flex items-start gap-1 text-[11px] italic', mine ? 'opacity-85' : 'text-muted-foreground')}>
            <Mic className="mt-0.5 h-3 w-3 shrink-0" />
            “{item.transcript}”
          </p>
        ) : null}
      </div>
    );
  }
  if (isVideo && url) {
    return <video controls preload="none" src={url} className="max-h-64 max-w-full rounded-lg ring-1 ring-border" />;
  }
  const label = item.filename || item.caption || item.kind || 'attachment';
  const chip = (
    <span
      className={cn(
        'inline-flex max-w-[240px] items-center gap-1 rounded-full px-2 py-1 text-[11px] font-medium ring-1 ring-inset',
        mine ? 'bg-primary-foreground/15 ring-primary-foreground/30' : 'bg-surface ring-border text-muted-foreground',
      )}
      title={item.mime_type || undefined}
    >
      {mime === 'application/pdf' ? <FileText className="h-3 w-3 shrink-0" /> : <Paperclip className="h-3 w-3 shrink-0" />}
      <span className="truncate">{label}</span>
    </span>
  );
  return url ? (
    <a href={url} target="_blank" rel="noreferrer" className="block">
      {chip}
    </a>
  ) : (
    chip
  );
}

function DeliveryTick({ status, error }: { status: string; error: string }) {
  if (status === 'failed') {
    return (
      <span className="inline-flex items-center gap-0.5 text-danger" title={error || 'Delivery failed'}>
        <AlertCircle className="h-3 w-3" /> failed
      </span>
    );
  }
  if (status === 'delivered') return <CheckCheck className="h-3 w-3" aria-label="Delivered" />;
  if (status === 'sent') return <Check className="h-3 w-3" aria-label="Sent" />;
  return <Clock className="h-3 w-3" aria-label="Pending" />;
}

// ---------------------------------------------------------------------------
// Composer: canned replies ("/" shortcut) + AI smart compose
// ---------------------------------------------------------------------------

function Composer({
  conv,
  value,
  onChange,
  onSend,
  sending,
}: {
  conv: CrmConversation;
  value: string;
  onChange: (v: string) => void;
  onSend: (text: string) => void | Promise<void>;
  sending: boolean;
}) {
  const canned = useCannedReplies();
  const suggest = useSuggestReply(conv.id);
  const [pickerOpen, setPickerOpen] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const slashQuery = value.startsWith('/') ? value.slice(1).toLowerCase() : null;
  const showPicker = pickerOpen || slashQuery !== null;
  const options = useMemo(() => {
    const all = (canned.data?.results ?? []).filter(
      (r) => r.channels.length === 0 || r.channels.includes(conv.channel),
    );
    const q = slashQuery ?? '';
    if (!q) return all;
    return all.filter(
      (r) =>
        r.shortcut.toLowerCase().includes(q) ||
        r.title.toLowerCase().includes(q) ||
        r.body.toLowerCase().includes(q),
    );
  }, [canned.data, conv.channel, slashQuery]);

  function applyCanned(reply: CrmCannedReply) {
    const name = (conv.contact?.display_name || '').split(' ')[0] || 'there';
    onChange(reply.body.replaceAll('{customer_name}', name));
    setPickerOpen(false);
    textareaRef.current?.focus();
  }

  async function smartCompose() {
    try {
      const res = await suggest.mutateAsync();
      if (res.reply) {
        onChange(res.reply);
        textareaRef.current?.focus();
      } else {
        notify.info('AI had no suggestion for this thread');
      }
    } catch {
      notify.error('Smart compose failed');
    }
  }

  const isClosed = conv.status === 'closed';
  return (
    <div className="relative border-t border-border p-2.5">
      {showPicker && options.length > 0 ? (
        <div className="absolute bottom-full left-2.5 right-2.5 z-10 mb-1 max-h-56 overflow-y-auto rounded-lg border border-border bg-surface shadow-lg">
          {options.map((r) => (
            <button
              key={r.id}
              type="button"
              onClick={() => applyCanned(r)}
              className="flex w-full flex-col gap-0.5 border-b border-border/50 px-3 py-2 text-left last:border-0 hover:bg-muted/60"
            >
              <span className="text-xs font-semibold">
                {r.title}
                {r.shortcut ? <span className="ml-2 font-normal text-muted-foreground">/{r.shortcut}</span> : null}
              </span>
              <span className="line-clamp-2 text-[11px] text-muted-foreground">{r.body}</span>
            </button>
          ))}
        </div>
      ) : null}

      <div className="flex items-end gap-2">
        <div className="flex flex-col gap-1">
          <button
            type="button"
            title="Canned replies (or type / in the box)"
            onClick={() => setPickerOpen((v) => !v)}
            className={cn(
              'rounded-md p-2 transition-colors',
              showPicker ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-muted',
            )}
          >
            <Zap className="h-4 w-4" />
          </button>
          <button
            type="button"
            title="AI smart compose — draft a reply for you"
            onClick={() => void smartCompose()}
            disabled={suggest.isPending}
            className={cn(
              'rounded-md p-2 text-muted-foreground transition-colors hover:bg-muted disabled:opacity-50',
              suggest.isPending && 'animate-pulse text-primary',
            )}
          >
            <Sparkles className="h-4 w-4" />
          </button>
        </div>
        <textarea
          ref={textareaRef}
          rows={value.split('\n').length > 2 ? 4 : 2}
          className="flex-1 resize-none rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary/50"
          placeholder={
            isClosed
              ? 'Conversation is closed — sending will not reopen it for the customer.'
              : 'Reply to customer…  ( /  canned replies · Enter to send · Shift+Enter newline )'
          }
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey && slashQuery === null) {
              e.preventDefault();
              void onSend(value);
            }
            if (e.key === 'Escape') setPickerOpen(false);
          }}
        />
        <Button
          className="gap-1"
          size="md"
          onClick={() => void onSend(value)}
          disabled={sending || !value.trim() || value.startsWith('/')}
        >
          <Send className="h-3.5 w-3.5" /> Send
        </Button>
      </div>
      {conv.channel === 'whatsapp_customer' && conv.last_customer_message_at ? (
        <WhatsAppWindowHint lastCustomerAt={conv.last_customer_message_at} />
      ) : null}
    </div>
  );
}

function WhatsAppWindowHint({ lastCustomerAt }: { lastCustomerAt: string }) {
  const hours = (Date.now() - new Date(lastCustomerAt).getTime()) / 36e5;
  if (hours < 24) {
    return (
      <p className="mt-1.5 text-[10px] text-muted-foreground">
        WhatsApp free-form window open (~{Math.max(0, 24 - hours).toFixed(1)}h left).
      </p>
    );
  }
  return (
    <p className="mt-1.5 text-[10px] text-warning-soft">
      WhatsApp 24h window expired — replies go out as the fallback template (configure under CRM
      Settings → Meta Channels) until the customer messages again.
    </p>
  );
}
